import { Injectable } from '@angular/core';
import * as signalR from '@microsoft/signalr';
import { BehaviorSubject, Observable } from 'rxjs';

export enum ConnectionState {
  Connecting = 'Connecting',
  Connected = 'Connected',
  Reconnecting = 'Reconnecting',
  Disconnected = 'Disconnected'
}

export interface TaskPickedUpEvent {
  taskId: number;
  userName: string;
  status: string;
  timestamp: Date;
}

export interface TaskStatusChangedEvent {
  taskId: number;
  oldStatus: string;
  newStatus: string;
  userName: string;
  timestamp: Date;
}

export interface TaskUpdatedEvent {
  taskId: number;
  taskData: any;
  timestamp: Date;
}

export interface FileEvent {
  taskId: number;
  fileName: string;
  userName: string;
  timestamp: Date;
}

export interface SapNumberUpdatedEvent {
  orderId: number;
  sapNumbers: ISapNumber[];
  timestamp: Date;
}

export interface CommunicationPostedEvent {
  orderId: number;
  messageId: number;
  userId: number;
  userName: string;
  message: string;
  timestamp: Date;
}

export interface DeploymentBulletinEvent {
  message: string;
  countdownSeconds: number;
  sentBy: string;
  timestamp: Date;
}

export interface ISapNumber {
  id?: number;
  orderId?: number;
  sapNumTypeId?: number;
  number?: number;
  createdBy?: number;
  createdDate?: Date;
  modifiedBy?: number;
  modifiedDate?: Date;
  description?: string;
  sapNumType?: { id?: number; name?: string };
}

@Injectable({
  providedIn: 'root'
})
export class SignalRService {
  private hubConnection: signalR.HubConnection | null = null;
  private connectionState$ = new BehaviorSubject<ConnectionState>(ConnectionState.Disconnected);
  
  // Event observables
  private taskPickedUp$ = new BehaviorSubject<TaskPickedUpEvent | null>(null);
  private taskStatusChanged$ = new BehaviorSubject<TaskStatusChangedEvent | null>(null);
  private taskUpdated$ = new BehaviorSubject<TaskUpdatedEvent | null>(null);
  private fileUploaded$ = new BehaviorSubject<FileEvent | null>(null);
  private fileDeleted$ = new BehaviorSubject<FileEvent | null>(null);
  private sapNumberUpdated$ = new BehaviorSubject<SapNumberUpdatedEvent | null>(null);
  private communicationPosted$ = new BehaviorSubject<CommunicationPostedEvent | null>(null);
  private deploymentBulletin$ = new BehaviorSubject<DeploymentBulletinEvent | null>(null);

  constructor() {}

  /**
   * Start SignalR connection
   * @param hubUrl - The URL of the SignalR hub (e.g., '/taskHub')
   */
  public startConnection(hubUrl: string = '/taskHub'): Promise<void> {
    if (this.hubConnection) {
      console.log('SignalR connection already exists, state:', this.hubConnection.state);
      if (this.hubConnection.state === signalR.HubConnectionState.Connected) {
        return Promise.resolve();
      }
    }

    console.log('Starting SignalR connection to:', hubUrl);

    this.hubConnection = new signalR.HubConnectionBuilder()
      .withUrl(hubUrl, {
        skipNegotiation: false,
        transport: signalR.HttpTransportType.WebSockets | signalR.HttpTransportType.ServerSentEvents | signalR.HttpTransportType.LongPolling
      })
      .withAutomaticReconnect({
        nextRetryDelayInMilliseconds: (retryContext) => {
          // Exponential backoff: 0, 2, 10, 30 seconds, then 30 seconds
          if (retryContext.elapsedMilliseconds < 60000) {
            return Math.random() * 10000;
          } else {
            return 30000;
          }
        }
      })
      .configureLogging(signalR.LogLevel.Information)
      .build();

    this.setupEventHandlers();
    this.setupConnectionHandlers();

    this.connectionState$.next(ConnectionState.Connecting);

    return this.hubConnection
      .start()
      .then(() => {
        console.log('✅ SignalR Connected successfully');
        this.connectionState$.next(ConnectionState.Connected);
      })
      .catch((err) => {
        console.error('❌ Error while starting SignalR connection: ', err);
        console.error('Connection URL:', hubUrl);
        console.error('Make sure the backend server is running on https://localhost:7003');
        this.connectionState$.next(ConnectionState.Disconnected);
        throw err;
      });
  }

  /**
   * Stop SignalR connection
   */
  public stopConnection(): Promise<void> {
    if (!this.hubConnection) {
      return Promise.resolve();
    }

    return this.hubConnection
      .stop()
      .then(() => {
        console.log('SignalR Disconnected');
        this.connectionState$.next(ConnectionState.Disconnected);
        this.hubConnection = null;
      })
      .catch((err) => {
        console.error('Error while stopping SignalR connection: ', err);
        throw err;
      });
  }

  /**
   * Join a task-specific group for targeted notifications
   */
  public joinTaskGroup(taskId: number): Promise<void> {
    if (!this.hubConnection) {
      return Promise.reject('SignalR connection not established');
    }

    return this.hubConnection.invoke('JoinTaskGroup', taskId)
      .catch((err) => {
        console.error(`Error joining task group ${taskId}:`, err);
        throw err;
      });
  }

  /**
   * Leave a task-specific group
   */
  public leaveTaskGroup(taskId: number): Promise<void> {
    if (!this.hubConnection) {
      return Promise.reject('SignalR connection not established');
    }

    return this.hubConnection.invoke('LeaveTaskGroup', taskId)
      .catch((err) => {
        console.error(`Error leaving task group ${taskId}:`, err);
        throw err;
      });
  }

  /**
   * Join an order-specific group for SAP number updates
   * Anyone on the EJDF page for this order will receive updates
   */
  public joinOrderGroup(orderId: number): Promise<void> {
    if (!this.hubConnection) {
      return Promise.reject('SignalR connection not established');
    }

    console.log(`📡 Joining order group: Order-${orderId}`);
    return this.hubConnection.invoke('JoinOrderGroup', orderId)
      .then(() => {
        console.log(`✅ Joined order group: Order-${orderId}`);
      })
      .catch((err) => {
        console.error(`Error joining order group ${orderId}:`, err);
        throw err;
      });
  }

  /**
   * Leave an order-specific group
   */
  public leaveOrderGroup(orderId: number): Promise<void> {
    if (!this.hubConnection) {
      return Promise.reject('SignalR connection not established');
    }

    console.log(`📡 Leaving order group: Order-${orderId}`);
    return this.hubConnection.invoke('LeaveOrderGroup', orderId)
      .then(() => {
        console.log(`✅ Left order group: Order-${orderId}`);
      })
      .catch((err) => {
        console.error(`Error leaving order group ${orderId}:`, err);
        throw err;
      });
  }

  /**
   * Setup event handlers for SignalR messages
   */
  private setupEventHandlers(): void {
    if (!this.hubConnection) return;

    this.hubConnection.on('TaskPickedUp', (data: TaskPickedUpEvent) => {
      console.log('📨 SignalR Event Received - TaskPickedUp:', JSON.stringify(data, null, 2));
      this.taskPickedUp$.next(data);
    });

    this.hubConnection.on('TaskStatusChanged', (data: TaskStatusChangedEvent) => {
      console.log('📨 SignalR Event Received - TaskStatusChanged:', JSON.stringify(data, null, 2));
      this.taskStatusChanged$.next(data);
    });

    this.hubConnection.on('TaskUpdated', (data: TaskUpdatedEvent) => {
      console.log('📨 SignalR Event Received - TaskUpdated:', JSON.stringify(data, null, 2));
      this.taskUpdated$.next(data);
    });

    this.hubConnection.on('FileUploaded', (data: FileEvent) => {
      console.log('📨 SignalR Event Received - FileUploaded:', JSON.stringify(data, null, 2));
      this.fileUploaded$.next(data);
    });

    this.hubConnection.on('FileDeleted', (data: FileEvent) => {
      console.log('📨 SignalR Event Received - FileDeleted:', JSON.stringify(data, null, 2));
      this.fileDeleted$.next(data);
    });

    this.hubConnection.on('SapNumberUpdated', (data: SapNumberUpdatedEvent) => {
      console.log('📨 SignalR Event Received - SapNumberUpdated:', JSON.stringify(data, null, 2));
      this.sapNumberUpdated$.next(data);
    });

    this.hubConnection.on('CommunicationPosted', (data: CommunicationPostedEvent) => {
      console.log('📨 SignalR Event Received - CommunicationPosted:', JSON.stringify(data, null, 2));
      this.communicationPosted$.next(data);
    });

    this.hubConnection.on('DeploymentBulletin', (data: DeploymentBulletinEvent) => {
      console.log('🚨 SignalR Event Received - DeploymentBulletin:', JSON.stringify(data, null, 2));
      this.deploymentBulletin$.next(data);
    });
  }

  /**
   * Setup connection state handlers
   */
  private setupConnectionHandlers(): void {
    if (!this.hubConnection) return;

    this.hubConnection.onreconnecting((error) => {
      console.warn('SignalR Reconnecting...', error);
      this.connectionState$.next(ConnectionState.Reconnecting);
    });

    this.hubConnection.onreconnected((connectionId) => {
      console.log('SignalR Reconnected. Connection ID:', connectionId);
      this.connectionState$.next(ConnectionState.Connected);
    });

    this.hubConnection.onclose((error) => {
      console.error('SignalR Connection Closed', error);
      this.connectionState$.next(ConnectionState.Disconnected);
    });
  }

  // Observable getters
  public get connectionState(): Observable<ConnectionState> {
    return this.connectionState$.asObservable();
  }

  public get onTaskPickedUp(): Observable<TaskPickedUpEvent | null> {
    return this.taskPickedUp$.asObservable();
  }

  public get onTaskStatusChanged(): Observable<TaskStatusChangedEvent | null> {
    return this.taskStatusChanged$.asObservable();
  }

  public get onTaskUpdated(): Observable<TaskUpdatedEvent | null> {
    return this.taskUpdated$.asObservable();
  }

  public get onFileUploaded(): Observable<FileEvent | null> {
    return this.fileUploaded$.asObservable();
  }

  public get onFileDeleted(): Observable<FileEvent | null> {
    return this.fileDeleted$.asObservable();
  }

  public get onSapNumberUpdated(): Observable<SapNumberUpdatedEvent | null> {
    return this.sapNumberUpdated$.asObservable();
  }

  public get onCommunicationPosted(): Observable<CommunicationPostedEvent | null> {
    return this.communicationPosted$.asObservable();
  }

  public get onDeploymentBulletin(): Observable<DeploymentBulletinEvent | null> {
    return this.deploymentBulletin$.asObservable();
  }

  /**
   * Check if connection is established
   */
  public isConnected(): boolean {
    return this.hubConnection?.state === signalR.HubConnectionState.Connected;
  }

  /**
   * Get current connection state
   */
  public getConnectionState(): ConnectionState {
    return this.connectionState$.value;
  }
}
